/* The Client Cockpit (client-first IA increment 2, spec §2 · §7 step 2).
 *
 * Route: #/client/{slug} with a header band (identity · needs-attention strip ·
 * Generate ▾ · data-refresh history — the run picker DEMOTED per Design note 1)
 * and tabs Overview / Board / Activity.
 *   • Overview = the Client Hub Overview (views/cockpit/overview.ts) — the
 *     presence-structured spine (header · program position · at-a-glance ·
 *     entity core · presence map · Wikipedia card · reputation surfaces · what
 *     needs improving · coverage) + the narrow right CRM sidecard rail.
 *   • Board = the existing board component, client-scoped (renderActions →
 *     #actionsBody, relocated into the tab; ADR-009 — no board edits).
 *   • Activity = the what-changed timeline from GET /clients/{slug}/changes
 *     (honest v1; per-task event history stays in the board's task drawer).
 *
 * Data comes through ONE seam — cockpitData() (views/cockpit/data.ts) — the
 * point the GET /clients/{slug}/overview feed swaps in later.
 */
import { html, nothing, render, type TemplateResult } from 'lit';
import { currentRoute, type CockpitTab, type Route } from '../../lib/router.ts';
import { beginLoad, isLoadCurrent, loadPayload, payloadStore } from '../../state/payload.ts';
import { wsChangeStore } from '../../state/wsChange.ts';
import { WS } from '../../state/workspace.ts';
import { parseAttention, type AttentionTone } from '../../lib/attention.ts';
import { listRuns, type RunListEntry } from '../../api/research.ts';
import { getChanges, type ChangesResponse } from '../../api/changes.ts';
import { getChangesSummary, type ChangesSummaryResponse } from '../../api/changesSummary.ts';
import { exportDataBundle } from '../../ui/exports.ts';
import { exportActionsCsv, renderActions } from '../../views/actions.ts';
import { mountOverviewIslands, renderOverview, teardownOverview } from './overview.ts';
import { closePropertyDrawer } from './propertyDrawer.ts';
import { renderWikiWorkroom, resetWikiWorkroom } from './wikiWorkroom.ts';
import { renderAiPage, renderNewsPage, renderSearchPage } from './deepPages.ts';
import { renderPlanPage, resetPlan } from './plan.ts';
import { renderSearch } from '../search.ts';
import { renderAI } from '../ai.ts';
import { cockpitData, type CockpitData } from './data.ts';
import { clientRunArtifacts, parseRunName } from '../../derive/deliverables.ts';

const TONE_CLASS: Record<AttentionTone, string> = { red: 'red', amber: 'amber', violet: 'violet', quiet: 'quiet' };

interface CockpitCurrent {
  slug: string;
  tab: CockpitTab;
  wsf: string | null;
  data: CockpitData;
  changes?: ChangesResponse | null;
  changesLoading?: boolean;
  /** the §1 what-changed FEED (GET /changes/summary) — distinct from the
   * Activity tab's full `changes` diff. `undefined` = not fetched yet. */
  summary?: ChangesSummaryResponse | null;
  summaryLoading?: boolean;
}

let current: CockpitCurrent | null = null;
let subscribed = false;

function inCockpit(): boolean {
  return currentRoute().area === 'cockpit';
}

/* ── run picker DEMOTED → per-client refresh history (Design note 1) ──────── */
let runsCache: RunListEntry[] | null = null;
// Run-name parsing lives in derive/deliverables.ts (ONE source of truth for the
// `{slug}-YYYYMMDD-HHMMSS-result.json` grammar, shared with the Deliverables tab).
function prettyRun(name: string): { date: string; label: string } {
  const p = parseRunName(name);
  return p ? { date: p.date, label: p.label } : { date: '', label: name.replace(/\.json$/, '') };
}
function clientRuns(slug: string): RunListEntry[] {
  return (runsCache || []).filter((r) => parseRunName(r.name || '')?.slug === slug);
}

/** Load an archived run into the store + repaint (shared by the header's
 * refresh-history dropdown and the Overview §6 receipts). Best-effort: a fetch
 * failure leaves the cockpit on the current run. */
function loadClientRun(name: string): void {
  // FIX-audit F3: allocate the load token at USER-ACTION start (the click),
  // before the fetch — allocating it after the fetch resolved let a stale load
  // become the newest generation and win. Commit only while this token is still
  // current AND we're still on the SAME client's cockpit (never paint one
  // client's archived run under another).
  const slug = current?.slug;
  const token = beginLoad();
  void (async () => {
    try {
      const res = await fetch(new URL('/test-runs/' + name, location.origin).toString());
      if (!res.ok) return;
      const text = await res.text();
      if (!isLoadCurrent(token) || !inCockpit() || current?.slug !== slug) return; // superseded
      closePropertyDrawer(); // F9: a new run's data must not show under the old run's open drawer
      loadPayload(text, name);
      paint();
    } catch { /* ignore — stays on current run */ }
  })();
}

/* ── the board tab mounts the EXISTING board (relocate #actionsBody) ──────── */
let boardMounted = false;
function mountBoard(host: HTMLElement): void {
  const body = document.getElementById('actionsBody');
  if (!body) return;
  host.appendChild(body);
  boardMounted = true;
  renderActions(payloadStore.get()); // paint the live board (WS-subscribed thereafter)
}
function unmountBoard(): void {
  if (!boardMounted) return;
  const body = document.getElementById('actionsBody');
  const home = document.getElementById('s7');
  if (body && home) home.appendChild(body);
  boardMounted = false;
}

/* ── deep-surface canvas mount (Package 4 — contract fix: NO node relocation) ──
 * The Search/AI pages render the app's REAL IMPACT/AIQ renderer into their OWN
 * mount (#dpCanvasHost) by passing it as the render target — renderSearch(state,
 * host) / renderAI(state, host). The renderers scope their sub-id lookups to that
 * root (views/mountRoot.ts), so the deep page and the legacy #serpBody/#aiBody
 * are two independent mount points, no DOM node is moved, and no restore logic is
 * needed. The legacy #/research/{search,ai} sections re-render into their own home
 * mounts on entry (main.ts). */
function mountDeepCanvas(tab: 'search' | 'ai', state: ReturnType<typeof payloadStore.get>): void {
  const host = document.getElementById('dpCanvasHost') as HTMLElement | null;
  if (!host) return;
  if (tab === 'search') renderSearch(state, host); else renderAI(state, host);
}

/* ── Generate ▾ (spec §4) — wired to the EXISTING export actions ──────────── */
function generateMenu(slug: string): TemplateResult {
  const close = (ev: Event): void => {
    const d = (ev.currentTarget as HTMLElement).closest('details');
    if (d) (d as HTMLDetailsElement).open = false;
  };
  return html`<details class="ck-gen">
    <summary class="ck-genbtn">Generate ▾</summary>
    <div class="ck-genmenu" role="menu">
      <a role="menuitem" class="ck-genitem" href="/clients/${slug}/progress-report" target="_blank" @click=${close}>Client progress report</a>
      <button role="menuitem" class="ck-genitem" @click=${(ev: Event) => { close(ev); exportDataBundle(payloadStore.get()); }}>Export for Claude (JSON)</button>
      <button role="menuitem" class="ck-genitem" @click=${(ev: Event) => { close(ev); exportActionsCsv(payloadStore.get()); }}>Export CSV</button>
    </div>
  </details>`;
}

function refreshHistory(slug: string, data: CockpitData): TemplateResult {
  const runs = clientRuns(slug);
  const last = data.row?.lastRun ? data.row.lastRun.slice(0, 10) : (runs[0] ? prettyRun(runs[0].name).date : null);
  return html`<details class="ck-hist">
    <summary class="ck-histbtn" title="this client's data-refresh history (the run picker, demoted per the client-first IA)">
      ${last ? `Refreshed ${last}` : 'No refresh yet'} ▾</summary>
    <div class="ck-histmenu" role="menu">
      <div class="ck-histhead">Data-refresh history</div>
      ${runs.length
        ? runs.map((r) => html`<button role="menuitem" class="ck-histitem" @click=${() => {
            const d = (document.querySelector('.ck-hist') as HTMLDetailsElement); if (d) d.open = false;
            loadClientRun(r.name);
          }}>${prettyRun(r.name).label}</button>`)
        : html`<div class="ck-histempty">No archived runs for this client yet.</div>`}
      <div class="ck-histitem ck-disabled" aria-disabled="true">
        <b>Refresh all…</b><span class="ck-refcost">$2–8 · ~10–40m · coming with the refresh-dispatch increment</span></div>
    </div>
  </details>`;
}

/* ── header band ──────────────────────────────────────────────────────────── */
/* Client identity mark: the verified brand logo (payload.brandLogo — Wikidata
 * P154 enrich, often a data: URI) with a NEUTRAL NON-LETTER fallback — never
 * placeholder initials (handoff §5 / CIW ClientBrandV1: no monogram field by
 * contract). The fallback is a neutral globe glyph, not a letter tile. */
const GLOBE_MARK: TemplateResult = html`<svg class="ck-globe" viewBox="0 0 24 24" aria-hidden="true">
  <circle cx="12" cy="12" r="9"></circle><path d="M3 12h18"></path>
  <path d="M12 3c2.8 2.6 2.8 15.4 0 18M12 3c-2.8 2.6-2.8 15.4 0 18"></path></svg>`;
/* FIX-audit F9: use payloadStore identity (brandLogo/brand) ONLY when the loaded
 * run is validated for THIS client (data.payloadLoaded). For an unknown/no-run
 * client the global store may still hold the PREVIOUS client's payload, which
 * would otherwise show client A's logo/brand on client B. */
function clientMark(payloadLoaded: boolean): TemplateResult {
  const logo = payloadLoaded ? payloadStore.get().brandLogo : null;
  return logo
    ? html`<img class="ck-logo" src=${logo} alt="" loading="lazy">`
    : html`<span class="ck-logo ck-logo-none" title="no verified logo on record">${GLOBE_MARK}</span>`;
}
function attentionStrip(slug: string, attention: string): TemplateResult {
  const frags = parseAttention(attention);
  return html`<div class="ck-attention">${frags.map((f, i) => html`${i > 0
    ? html`<span class="ck-sep" aria-hidden="true"> · </span>` : nothing}<a
      class="ck-frag ${TONE_CLASS[f.tone]}"
      href="#/client/${slug}/board${f.wsf ? `?wsf=${encodeURIComponent(f.wsf)}` : ''}"
    >${f.text}</a>`)}</div>`;
}

function header(data: CockpitData, slug: string): TemplateResult {
  // Prefer the rollup row's brand; fall back to the loaded payload's brand ONLY
  // when that run is validated for THIS client (F9), else the slug.
  const brand = data.row?.brand || (data.payloadLoaded ? payloadStore.get().brand : null) || slug;
  return html`<header class="ck-header">
    <a class="ck-back" href="#/clients" title="back to My Clients">← My Clients</a>
    <div class="ck-idrow">
      <div class="ck-id">
        ${clientMark(data.payloadLoaded)}
        <h1 class="ck-brand">${brand}</h1>
      </div>
      <div class="ck-headactions">
        ${refreshHistory(slug, data)}
        ${generateMenu(slug)}
      </div>
    </div>
    ${data.row?.attention ? attentionStrip(slug, data.row.attention) : nothing}
  </header>`;
}

/* The deep-surface tabs that live UNDER the anatomy's "Intelligence" nav item
 * (§4.2). They render their OWN full-page chrome (surfaceContextBar / plan's
 * context bar), so this tab bar is never shown while one of them is active — the
 * mapping exists so "Intelligence" reads as the active client-nav item and its
 * entry point is the primary anchor (§4.5: Search leads the three anchors). */
const INTELLIGENCE_TABS: readonly CockpitTab[] = ['search', 'news', 'ai', 'wiki', 'plan'];

/* Client navigation aligned to anatomy §4.2 (Overview · Intelligence · Tasks ·
 * Requests · Activity · Deliverables), mapped onto the existing routes with NO
 * breakage: Tasks IS the board route (/board), Intelligence enters the deep
 * surfaces at /search, and every legacy route keeps resolving. */
function tabsBar(slug: string, tab: CockpitTab): TemplateResult {
  const item = (label: string, hrefSuffix: string, on: boolean): TemplateResult => html`
    <a class="ck-tab ${on ? 'on' : ''}" role="tab" aria-selected=${on ? 'true' : 'false'}
      href="#/client/${slug}${hrefSuffix}">${label}</a>`;
  return html`<nav class="ck-tabs" role="tablist" aria-label="client navigation">
    ${item('Overview', '', tab === 'overview')}
    ${item('Intelligence', '/search', INTELLIGENCE_TABS.includes(tab))}
    ${item('Tasks', '/board', tab === 'board')}
    ${item('Requests', '/requests', tab === 'requests')}
    ${item('Activity', '/activity', tab === 'activity')}
    ${item('Deliverables', '/deliverables', tab === 'deliverables')}
  </nav>`;
}

/* ── tab bodies ───────────────────────────────────────────────────────────── */
function overviewBody(slug: string, data: CockpitData): TemplateResult {
  const state = payloadStore.get();
  if (!data.payloadLoaded || data.noResearch) {
    return html`<div class="ck-empty">
      <p>No research has been run for this client yet.</p>
      <p class="ck-muted">The client-first Overview — program state, entities, the IMPACT/AIQ anchors, findings, and
        tasks — fills in once the first run lands.</p>
    </div>`;
  }
  return renderOverview({
    ws: WS, state, row: data.row, slug, rerender: () => paint(),
    changes: current?.summary ?? null,
    changesLoading: !!current?.summaryLoading,
  });
}

function activityBody(slug: string): TemplateResult {
  if (!current || current.slug !== slug) return html`<div class="ck-muted">Loading…</div>`;
  if (current.changesLoading) return html`<div class="ck-muted">Loading what changed…</div>`;
  const ch = current.changes;
  if (!ch) {
    return html`<div class="ck-empty"><p>Activity timeline unavailable.</p>
      <p class="ck-muted">Per-task history (who did what, when) lives in each task's row on the Board.</p></div>`;
  }
  if (!ch.comparable) {
    return html`<div class="ck-empty"><p>Nothing to compare yet — ${ch.reason || 'only one run on record'}.</p>
      <p class="ck-muted">The what-changed timeline appears once this client has two runs. Per-task history is on the Board.</p></div>`;
  }
  const rows: TemplateResult[] = [];
  const push = (label: string, detail: string): void => { rows.push(html`<li class="ck-actrow"><span class="ck-actlabel">${label}</span><span class="ck-actdetail">${detail}</span></li>`); };
  if (ch.assets) push('Surfaces', `${ch.assets.added.length} added · ${ch.assets.removed.length} removed · ${ch.assets.changed.length} changed`);
  if (ch.opportunities) push('Opportunities', `${ch.opportunities.new.length} new · ${ch.opportunities.closed.length} closed`);
  if (ch.conflicts) push('Conflicts', `${ch.conflicts.new.length} new · ${ch.conflicts.resolved.length} resolved`);
  if (ch.ai && typeof ch.ai.shareDelta === 'number') push('AI narrative', `citation share ${ch.ai.shareDelta >= 0 ? '+' : ''}${ch.ai.shareDelta}`);
  if (ch.search && ch.search.perTerm.length) {
    const moved = ch.search.perTerm.filter((t) => t.nowOwned !== t.prevOwned).length;
    push('Search results', `${moved} term×geo cell${moved === 1 ? '' : 's'} moved`);
  }
  const when = `${ch.from?.generatedAt?.slice(0, 10) || '?'} → ${ch.to?.generatedAt?.slice(0, 10) || '?'}`;
  return html`<div class="ck-activity">
    <div class="ck-actwhen">Changes between runs · ${when}</div>
    ${rows.length ? html`<ul class="ck-actlist">${rows}</ul>` : html`<p class="ck-muted">No material changes between the last two runs.</p>`}
    ${ch.narrative ? html`<p class="ck-actnarr">${ch.narrative}</p>` : nothing}
    <p class="ck-muted ck-actnote">Honest v1: this timeline is the run-over-run delta. Per-task event history (status moves, notes, probes) is on each task's Board row — a fuller task-event timeline rides a later increment.</p>
  </div>`;
}

/* ── Requests tab (anatomy §4.2) — DISCOVERY: no request-typed record exists in
 * the ledger/service (no requests table; `requested_by` on recheck/decision rows
 * is unrelated). Requests are client-facing asks, distinct from internal Tasks —
 * with no request channel wired this is a DESIGNED HONEST EMPTY (standard
 * empty-card grammar), never a fake create-flow or local-only storage. ───────── */
function requestsBody(slug: string): TemplateResult {
  return html`<div class="ck-empty">
    <p>No client requests are tracked yet.</p>
    <p class="ck-muted">Requests are client-facing asks — distinct from the internal work on
      <a class="ck-inline-link" href="#/client/${slug}/board">Tasks</a>. This channel isn't wired to a data
      source yet; nothing here is fabricated.</p>
    <section class="empty-state-card">
      <span class="eyebrow">What this surface will show — once wired</span>
      <ul class="empty-state-list">
        <li><b>Client-facing asks</b> — requests raised by or for the client, in the same list grammar as My Work
          rows (state · waiting-on · age).</li>
        <li><b>Distinct from Tasks</b> — internal work stays on Tasks; a request may spawn one or more tasks, but
          the ask itself is its own record.</li>
        <li><b>Sourced state</b> — where each request sits, from the request channel — never a fabricated stage.</li>
      </ul>
      <div class="empty-state-note"><b>Not wired — no request channel is connected to this route yet.</b>
        Client-facing asks will live here once the request channel is wired; internal work lives in Tasks. When a
        source lands this fills with real records, never demo data.</div>
    </section>
  </div>`;
}

/* ── Deliverables tab (anatomy §4.2 / §8) — DISCOVERY: the external-artifact
 * registry (`external_artifacts`, Drive/Jira/meeting) is Phase 0 — no adapter
 * populates it and no endpoint lists its rows, so there is no external-deliverable
 * data yet. What IS real: this client's archived research-run results (GET /runs
 * → local[]), dated and downloadable. Render those honestly labeled; the external
 * registry is described as the honest not-wired state below. (derive/deliverables.ts) */
function deliverablesBody(slug: string): TemplateResult {
  const arts = clientRunArtifacts(slug, runsCache || []);
  return html`<div class="ck-deliv">
    <p class="ck-muted ck-deliv-lede">Generated artifacts from this client's research runs — downloadable, dated.
      The external deliverables registry (Drive files, decks and other externally created artifacts) lands later.</p>
    ${arts.length
      ? html`<ul class="ck-actlist ck-deliv-list">
          ${arts.map((a) => html`<li class="ck-deliv-row">
            <span class="ck-deliv-kind">Research run</span>
            <span class="ck-deliv-name">${a.label}${a.sizeKb ? html` · <small>${a.sizeKb} KB</small>` : nothing}</span>
            <a class="ck-deliv-dl" href=${a.href} download title="download this run's result JSON">Download JSON</a>
          </li>`)}
        </ul>`
      : html`<p class="ck-muted ck-deliv-none">No generated artifacts yet — a research run's result becomes a
          downloadable artifact here once the first run lands.</p>`}
    <section class="empty-state-card ck-deliv-ext">
      <span class="eyebrow">External deliverables registry — once wired</span>
      <ul class="empty-state-list">
        <li><b>Externally created artifacts</b> — Drive files, decks and reports produced outside the platform,
          with type, period, owner and link.</li>
        <li><b>Delivery state</b> — working draft · internally approved · shared · superseded, sourced or
          human-attested — never assumed.</li>
        <li><b>Linked to work</b> — each artifact tied to the source objects and the tasks/outcomes it delivered.</li>
      </ul>
      <div class="empty-state-note"><b>Not wired — no external-artifact source (Drive) is connected yet.</b>
        The list above is derived from research-run archives only; nothing about external delivery is fabricated.</div>
    </section>
  </div>`;
}

/* ── paint ─────────────────────────────────────────────────────────────────── */
function paint(): void {
  const host = document.getElementById('cockpitView');
  if (!host || !current) return;
  unmountBoard();

  const { slug, tab, data } = current;

  // Deep-surface pages (Wikipedia · Search · News · AI) are full pages with their
  // OWN context bar — they do NOT use the Overview/Board/Activity header or tabs.
  if (!data.error && (tab === 'wiki' || tab === 'search' || tab === 'news' || tab === 'ai' || tab === 'plan')) {
    const state = payloadStore.get();
    const payloadLoaded = data.payloadLoaded && !data.noResearch;
    // F9: the payload's brand is only trustworthy for THIS client once its run is
    // validated as loaded; otherwise fall back to the rollup row or the slug.
    const brand = data.row?.brand || (payloadLoaded ? state.brand : null) || slug;
    if (tab === 'wiki') {
      render(renderWikiWorkroom({ state, brand, slug, payloadLoaded, rerender: () => paint() }), host);
      return;
    }
    // Intelligence Plan (W1-C): a deep surface like wiki — its own chrome, no
    // relocated IMPACT/AIQ canvas. Reads the live WS ledger to match each
    // opportunity to its real opp:* task (re-paints on WS changes, see the
    // subscription below).
    if (tab === 'plan') {
      render(renderPlanPage({ state, brand, slug, payloadLoaded, ws: WS, rerender: () => paint() }), host);
      return;
    }
    const dd = { state, brand, slug, payloadLoaded, ws: WS };
    if (tab === 'news') { render(renderNewsPage(dd), host); return; }
    render(tab === 'search' ? renderSearchPage(dd) : renderAiPage(dd), host);
    // Render the REAL IMPACT/AIQ renderer INTO the page's own mount (no node
    // relocation) — the renderer scopes its sub-ids to this root (mountRoot.ts).
    if (payloadLoaded) mountDeepCanvas(tab, state);
    return;
  }

  let body: TemplateResult;
  if (data.error) {
    body = html`<div class="ck-empty"><p>Workspace service unreachable — start the service and reload.</p></div>`;
  } else if (tab === 'board') {
    body = html`<div class="ck-boardhost" id="ckBoardHost"></div>`;
  } else if (tab === 'activity') {
    body = activityBody(slug);
  } else if (tab === 'requests') {
    body = requestsBody(slug);
  } else if (tab === 'deliverables') {
    body = deliverablesBody(slug);
  } else {
    body = overviewBody(slug, data);
  }

  render(html`
    ${header(data, slug)}
    ${tabsBar(slug, tab)}
    <div class="ck-tabpanel" role="tabpanel">${body}</div>`, host);

  if (tab === 'board') {
    const bh = document.getElementById('ckBoardHost');
    if (bh) mountBoard(bh);
  } else if (tab === 'overview') {
    // Mount the presence-map Graph island (ui/graph.ts) into the freshly-
    // rendered #map svg when Graph mode is active — a no-op in Tree mode.
    mountOverviewIslands(payloadStore.get());
  }
}

/* ── activity data (lazy) ─────────────────────────────────────────────────── */
async function loadActivity(slug: string): Promise<void> {
  if (!current || current.slug !== slug) return;
  current.changesLoading = true;
  paint();
  // FIX-audit F7: resolve into a LOCAL, then commit only if we're STILL on the
  // same client. The start-of-function slug check runs BEFORE the await; without
  // re-checking after it, a completion for client A that lands after a switch to
  // B would write A's activity onto B's cockpit state and paint it.
  let changes: ChangesResponse | null = null;
  try { changes = await getChanges(slug, { narrative: false }); }
  catch { changes = null; }
  if (!current || current.slug !== slug) return; // switched clients mid-fetch — discard
  current.changes = changes;
  current.changesLoading = false;
  if (inCockpit() && current.tab === 'activity') paint();
}

/* ── §1 what-changed FEED (lazy, cached server-side) ──────────────────────── */
async function loadChangesSummary(slug: string): Promise<void> {
  if (!current || current.slug !== slug) return;
  current.summaryLoading = true;
  if (inCockpit() && current.tab === 'overview') paint();
  // FIX-audit F7: same owning-client guard after the await (see loadActivity).
  let summary: ChangesSummaryResponse | null = null;
  try { summary = await getChangesSummary(slug); }
  catch { summary = null; } // service unreachable — the block renders honestly
  if (!current || current.slug !== slug) return; // switched clients mid-fetch — discard
  current.summary = summary;
  current.summaryLoading = false;
  if (inCockpit() && current.tab === 'overview') paint();
}

/* ── wsf deep-link (My Clients fragment → Board tab pre-filtered) ──────────── */
function applyWsf(wsf: string | null): void {
  if (!wsf) return;
  const [kind, value] = wsf.split(':');
  if (kind && ['overdue', 'stalled', 'assignee'].includes(kind)) {
    WS._urlFilter = { kind: kind as 'overdue' | 'stalled' | 'assignee', value,
      label: kind === 'assignee' ? 'tasks w/ ' + value : kind + ' only' };
  }
}

/** Entry point (main.ts wires this on boot + on navigation into a cockpit). */
export async function initCockpit(route: Route): Promise<void> {
  const host = document.getElementById('cockpitView');
  if (!host || !route.slug) return;
  const slug = route.slug;
  // FIX-audit F3: allocate the load token at NAVIGATION START, before any await,
  // and thread it through cockpitData → loadRunPayload. A slower-resolving
  // EARLIER navigation (its /clients or run fetch landing last) can no longer
  // become the newest generation and commit its run under THIS route: the commit
  // is gated on isLoadCurrent(token), and the paint below additionally requires
  // isLoadCurrent(token) AND an exact route/client match.
  const token = beginLoad();
  // wsf carried in the hash query (#/client/{slug}/board?wsf=…) — the demoted
  // deep-link target from My Clients fragments.
  const q = (location.hash.split('?')[1] || '');
  const wsf = new URLSearchParams(q).get('wsf');
  const tab = route.tab || 'overview';

  // paint a light shell immediately (avoids a blank cockpit during the fetch)
  if (!current || current.slug !== slug) {
    // F9: a property drawer open on the PREVIOUS client must not survive the
    // switch (cockpit→cockpit navigation doesn't run teardownCockpit).
    closePropertyDrawer();
    current = { slug, tab, wsf, data: { slug, row: null, payloadLoaded: false, noResearch: false } };
    render(html`<div class="ck-tabpanel"><div class="ck-muted" style="padding:24px 0">Loading client…</div></div>`, host);
  } else {
    current.tab = tab; current.wsf = wsf;
  }

  // one-time run list (for the refresh-history dropdown) — best-effort.
  if (runsCache === null) { try { runsCache = (await listRuns()).local || []; } catch { runsCache = []; } }

  const data = await cockpitData(slug, token);
  if (!inCockpit() || currentRoute().slug !== slug || !isLoadCurrent(token)) return; // navigated away / superseded mid-fetch
  // carry the §1 what-changed feed across the rebuild when it's the same client
  // (a tab switch), so re-entering Overview doesn't re-flash "Loading…".
  const carrySummary = current && current.slug === slug ? current.summary : undefined;
  current = { slug, tab, wsf, data, summary: carrySummary };
  applyWsf(wsf);
  paint();
  if (tab === 'activity') void loadActivity(slug);
  // fetch the what-changed feed once per client (server memoizes; $0 to recompute)
  if (data.payloadLoaded && current.summary === undefined) void loadChangesSummary(slug);

  if (!subscribed) {
    subscribed = true;
    // Live-update the overview grid AND the Intelligence Plan on task/payload
    // changes. The board tab self-updates via renderActions' own wsChange
    // subscription; activity is static after its fetch — so overview + plan are
    // the two that need a re-paint here (the plan matches opportunities to live
    // opp:* ledger tasks, which arrive after WS.init()).
    const onChange = (): void => {
      if (inCockpit() && current && (current.tab === 'overview' || current.tab === 'plan')) paint();
    };
    wsChangeStore.subscribe(onChange);
    payloadStore.subscribe(onChange);
  }
}

/** Called by main.ts when navigating AWAY from a cockpit — returns any
 * relocated section body / the board to its home so #/work and #/research keep
 * working. */
export function teardownCockpit(): void {
  teardownOverview();
  resetWikiWorkroom();
  resetPlan();
  unmountBoard();
  current = null;
}
